/**
 * What the trainer has asked of a list of movements — the search text and the four facets — and
 * the dispatches that change it. Decides nothing: the narrowing is `exerciseCatalogue`'s, over
 * whichever movements the host hands it.
 *
 * Split out of `useExerciseLibraryQuery` on its second host, the Forma tab's list, which narrows
 * the movements a client has TRAINED rather than the whole catalogue and so has no read of its own
 * to wrap. One `useState` holding the whole query, for the reason that hook's header gives.
 */

import { useState } from 'react';

import { emptyExerciseQuery, type ExerciseQuery } from '../model/exerciseCatalogue';
import {
  removeExerciseFilter,
  toggleExerciseFacet,
  type ExerciseFacetKey,
} from '../model/exerciseFacets';

export interface ExerciseQueryState {
  readonly query: ExerciseQuery;
  readonly setText: (text: string) => void;
  /** A chip press, in any of the four groups. */
  readonly toggle: (facet: ExerciseFacetKey, id: string) => void;
  /** The × on an active-filter chip. Distinct from `toggle` only for single-select ORIGIN. */
  readonly remove: (facet: ExerciseFacetKey, id: string) => void;
  /** The facets back to nothing. The TEXT survives, deliberately — see below. */
  readonly clear: () => void;
  /** Text and facets both back to nothing. */
  readonly reset: () => void;
}

export function useExerciseQuery(): ExerciseQueryState {
  const [query, setQuery] = useState<ExerciseQuery>(emptyExerciseQuery);
  return {
    query,
    setText: (text) => {
      setQuery((current) => ({ ...current, text }));
    },
    toggle: (facet, id) => {
      setQuery((current) => toggleExerciseFacet(current, facet, id));
    },
    remove: (facet, id) => {
      setQuery((current) => removeExerciseFilter(current, facet, id));
    },
    clear: () => {
      // "Clear the filters" is about the chips, and wiping a search the trainer is halfway through
      // typing is not what that reads as.
      setQuery((current) => ({ ...emptyExerciseQuery(), text: current.text }));
    },
    reset: () => {
      setQuery(emptyExerciseQuery());
    },
  };
}
